import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { getUnsubscribeSubscriber, unsubscribeNewsletter } from "@/lib/api/newsletter.functions";
import { Container } from "@/components/site/Primitives";
import { FormActions } from "@/components/FormActions";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const OPT_OUT_REASONS = [
  "Too many emails",
  "Content is not relevant",
  "I never signed up",
  "I prefer other channels",
  "Other",
];

export const Route = createFileRoute("/newsletter/unsubscribe")({
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === "string" ? search.token : "",
  }),
  loader: async ({ location }) => {
    const token = new URLSearchParams(location.searchStr).get("token") ?? "";
    if (!token) return { subscriber: null, token: "" };
    const subscriber = await getUnsubscribeSubscriber({ data: { token } });
    return { subscriber, token };
  },
  component: UnsubscribePage,
});

function UnsubscribePage() {
  const { subscriber, token } = Route.useLoaderData();
  const [reason, setReason] = useState(OPT_OUT_REASONS[0]);
  const [otherReason, setOtherReason] = useState("");
  const [done, setDone] = useState(subscriber?.status === "unsubscribed");
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const finalReason = reason === "Other" ? otherReason.trim() : reason;
    if (!finalReason) {
      toast.error("Please tell us why you are unsubscribing");
      return;
    }
    setSubmitting(true);
    try {
      await unsubscribeNewsletter({ data: { token, reason: finalReason } });
      setDone(true);
      toast.success("You have been unsubscribed");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not unsubscribe");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Container className="py-20">
      <div className="mx-auto max-w-md">
        <h1 className="text-2xl font-bold text-primary">Unsubscribe</h1>
        {!token || !subscriber ? (
          <p className="mt-4 text-muted-foreground">This unsubscribe link is invalid or has expired.</p>
        ) : done ? (
          <p className="mt-4 text-muted-foreground">
            {subscriber.maskedEmail} has been removed from our newsletter list. You will no longer receive insight emails from us.
          </p>
        ) : (
          <>
            <p className="mt-4 text-muted-foreground">
              Unsubscribe <span className="font-medium text-foreground">{subscriber.maskedEmail}</span> from KS Lau &amp; Co insights emails.
            </p>
            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <div className="space-y-2">
                <Label>Reason for unsubscribing</Label>
                <Select value={reason} onValueChange={setReason}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {OPT_OUT_REASONS.map((r) => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {reason === "Other" ? (
                <div className="space-y-2">
                  <Label>Please specify</Label>
                  <Textarea value={otherReason} onChange={(e) => setOtherReason(e.target.value)} rows={3} required />
                </div>
              ) : null}
              <FormActions>
                <Button type="submit" disabled={submitting}>
                  {submitting ? "Unsubscribing…" : "Confirm unsubscribe"}
                </Button>
              </FormActions>
            </form>
          </>
        )}
      </div>
    </Container>
  );
}
